import React from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCloseEditInventoryModal } from "../../redux/features/inventory/inventorySlice";
import { useFormik } from "formik";
import { updateInventory } from "../../redux/features/inventory/inventoryAction";
import * as Yup from "yup";

const inventoryValidationSchema = Yup.object({
    // ean: Yup.string().matches(/^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/, "EAN deve contenere 8, 12, 13 o 14 cifre").required("EAN è obbligatorio"),

    sku: Yup.string()
        .required("SKU è obbligatorio")
        .matches(
            /^[A-Za-z0-9_-]{1,30}$/,
            "SKU può contenere solo lettere, numeri, trattini o underscore. Massimo 30 caratteri"
        )
        .test(
            "not-all-zeros",
            "SKU non può contenere solo zeri",
            value => value !== undefined && !/^0+$/.test(value.trim())
        ),
    totalQuantity: Yup.number().typeError("Quantità deve essere un numero").integer("Quantità deve essere un numero intero").min(1, "Minimo 1").max(1000, "Massimo 1.000").required("Quantità è obbligatoria"),
    prezzoVendita: Yup.number().typeError("Prezzo Vendita deve essere un numero valido").min(0.01, "Minimo 0.01").max(10000, "Massimo 10.000").required("Prezzo Vendita è obbligatorio"),
    costoArticoli: Yup.number().typeError("Costo Articoli deve essere un numero valido").min(0.01, "Minimo 0.01").max(5000, "Massimo 5.000").required("Costo Articoli è obbligatorio"),
    iva: Yup.number().typeError("IVA deve essere un numero valido").min(0, "IVA non può essere negativa").max(100, "IVA non può superare 100").required("IVA è obbligatoria"),
    productTargetMargin: Yup.number().typeError("Margine previsto deve essere un numero").min(0, "Minimo 0").max(100, "Massimo 100"),
});

const EditInventoryModal = () => {
    const dispatch: any = useDispatch();
    const { editInventoryModalOpen, editInventoryData } = useSelector(
        (state: any) => state.inventorySlice
    );

    const handleClose = () => {
        dispatch(setCloseEditInventoryModal());
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: editInventoryData?.id,
            sku: editInventoryData?.sku,
            ean: editInventoryData?.ean || "",
            costoArticoli: editInventoryData?.costoArticoli || "",
            prezzoVendita: editInventoryData?.prezzoVendita || "",
            iva: editInventoryData?.iva || "",
            productTargetMargin: editInventoryData?.productTargetMargin || 0,
            totalQuantity: editInventoryData?.totalQuantity || "",
        },
        validationSchema: inventoryValidationSchema,
        onSubmit: async (values) => {
            try {
                await dispatch(updateInventory({ values }));
                handleClose();
            } catch (error) {
                console.error("Update inventory failed:", error);
            }
        },
    });

    return (
        <Modal show={editInventoryModalOpen} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Modifica articolo di inventario</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={formik.handleSubmit}>

                    <Form.Group className="mb-3">
                        <Form.Label>SKU</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Identificatore univoco del prodotto. Solo lettere, numeri, trattini o underscore.<br />
                            Obbligatorio. Lunghezza massima: 30 caratteri. Deve essere univoco.
                        </div>
                        <Form.Control
                            type="text"
                            name="sku"
                            value={formik.values.sku}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.sku && !!formik.errors.sku}
                        // disabled
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.sku && (formik.errors.sku?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>EAN</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Codice a barre esattamente come scansionato (EAN-8 o EAN-13). Solo cifre.<br />
                            Solo 8 o 13 cifre. Nessuno spazio o simbolo.
                        </div>
                        <Form.Control
                            type="text"
                            name="ean"
                            value={formik.values.ean}
                            // onChange={formik.handleChange}
                            // isInvalid={formik.touched.ean && !!formik.errors.ean}
                            disabled
                        />
                        {/* <Form.Control.Feedback type="invalid">
                            {formik.touched.ean && (formik.errors.ean?.toString() ?? '')}
                        </Form.Control.Feedback> */}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Quantità</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Numero di unità vendute. Deve essere un numero intero positivo.<br />
                            Min: 1, Max: 1.000
                        </div>
                        <Form.Control
                            type="number"
                            name="totalQuantity"
                            value={formik.values.totalQuantity}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.totalQuantity && !!formik.errors.totalQuantity}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.totalQuantity && (formik.errors.totalQuantity?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Prezzo Vendita</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Prezzo di vendita unitario (IVA esclusa). Usa il punto `.` come separatore decimale.<br />
                            Min: 0.01, Max: 10.000,00
                        </div>
                        <Form.Control
                            type="text"
                            name="prezzoVendita"
                            value={formik.values.prezzoVendita}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.prezzoVendita && !!formik.errors.prezzoVendita}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.prezzoVendita && (formik.errors.prezzoVendita?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Costo Articoli</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Costo unitario per l'azienda (IVA esclusa). Deve essere ≤ al Prezzo di Vendita.<br />
                            Min: 0.01, Max: 5.000,00. Deve essere ≤ al Prezzo di Vendita.
                        </div>
                        <Form.Control
                            type="text"
                            name="costoArticoli"
                            value={formik.values.costoArticoli}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.costoArticoli && !!formik.errors.costoArticoli}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.costoArticoli && (formik.errors.costoArticoli?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>IVA</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Percentuale IVA, scritta come numero intero (senza simbolo %).<br />
                            Valori accettati: 0, 4, 10, 22
                        </div>
                        <Form.Control
                            type="text"
                            name="iva"
                            value={formik.values.iva}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.iva && !!formik.errors.iva}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.iva && (formik.errors.iva?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Margine previsto del prodotto (%)</Form.Label>
                        <div className="form-text text-muted mb-1">
                            Inserisci la percentuale di margine che desideri ottenere su ogni vendita, da 1% a 100%. Più competitiva sarà la tua offerta e maggiori saranno le tue possibilità di ricevere ordini. Consideralo come un sistema di aste: a parità di condizioni, i rivenditori con le offerte migliori hanno la priorità. Il sistema prenderà sempre il valore più basso tra il margine previsto di prodotto e quello target dell'account.
                        </div>
                        <Form.Control
                            type="number"
                            name="productTargetMargin"
                            value={formik.values.productTargetMargin}
                            onChange={formik.handleChange}
                            isInvalid={formik.touched.productTargetMargin && !!formik.errors.productTargetMargin}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.touched.productTargetMargin && (formik.errors.productTargetMargin?.toString() ?? '')}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Annulla
                        </Button>
                        <Button variant="primary" type="submit">
                            Salva modifiche
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default EditInventoryModal;
